import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';

class PresenceService {
  private static instance: PresenceService;
  private pendingUpdate: { userId: string; activityType: string } | null = null;
  private updateTimeout: NodeJS.Timeout | null = null;
  private lastUpdate: number = 0;
  private MIN_INTERVAL = 5000; // 5 segundos

  private constructor() {}

  static getInstance(): PresenceService {
    if (!PresenceService.instance) {
      PresenceService.instance = new PresenceService();
    }
    return PresenceService.instance;
  }

  async updatePresence(userId: string, activityType: string = 'general'): Promise<void> {
    const now = Date.now();
    
    // Ignorar se atualizou há menos de 5 segundos
    if (now - this.lastUpdate < this.MIN_INTERVAL) {
      debugLogger.debug('[PresenceService] Update throttled (too soon)', {
        userId,
        activityType,
        timeSinceLastUpdate: now - this.lastUpdate
      });
      return;
    }

    // Cancelar update pendente anterior
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    // Armazenar update pendente
    this.pendingUpdate = { userId, activityType };

    // Debounce de 500ms
    this.updateTimeout = setTimeout(() => {
      this.executeUpdate();
    }, 500);
  }

  private async executeUpdate(): Promise<void> {
    if (!this.pendingUpdate) return;

    const { userId, activityType } = this.pendingUpdate;
    this.pendingUpdate = null;
    this.updateTimeout = null;

    try {
      await supabase.rpc('update_user_presence', {
        user_id_param: userId,
        activity_type_param: activityType
      });

      this.lastUpdate = Date.now();
      
      debugLogger.debug('[PresenceService] Presence updated', {
        userId,
        activityType
      });
    } catch (error) {
      debugLogger.warn('[PresenceService] Failed to update presence:', error);
    }
  }

  async forceUpdate(userId: string, activityType: string): Promise<void> {
    // Limpar qualquer update pendente
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }

    try {
      await supabase.rpc('update_user_presence', {
        user_id_param: userId,
        activity_type_param: activityType
      });

      this.lastUpdate = Date.now();
      
      debugLogger.debug('[PresenceService] Presence force updated', {
        userId,
        activityType
      });
    } catch (error) {
      debugLogger.warn('[PresenceService] Failed to force update presence:', error);
    }
  }
}

export const presenceService = PresenceService.getInstance();
